import React, { useState, useEffect } from 'react';
import '../sidebar/MinhaConta.css';
import { apiCallAuth, getUsuarioLogado, isLoggedIn } from '../utils/auth';
import { useNavigate } from 'react-router-dom';

/* Componente para cadastro de conta de recebimento - Marketplace */
/* Fluxo completo: Dados Pessoais → Endereço → Conta Bancária → Stripe Custom Account */

export default function CadastrarConta({ onSuccess, onError, empresaId }) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [existingAccount, setExistingAccount] = useState(null);
  const [debugInfo, setDebugInfo] = useState({ hasToken: false, hasUser: false, userInfo: null });
  
  // Etapa do formulário: 1=dados pessoais, 2=endereço, 3=conta bancária, 4=confirmação
  const [step, setStep] = useState(1);
  
  // Campos do formulário - Dados Pessoais
  const [email, setEmail] = useState('');
  const [telefone, setTelefone] = useState('');
  const [tipoPessoa, setTipoPessoa] = useState(() => {
    try { const u = getUsuarioLogado(); return u?.tipoPessoa || 'PF'; } catch { return 'PF'; }
  });
  const [nome, setNome] = useState('');
  const [cpf, setCpf] = useState('');
  const [cnpj, setCnpj] = useState('');
  const [dataNascimento, setDataNascimento] = useState('');
  
  // Campos PJ - Representante Legal
  const [representanteNome, setRepresentanteNome] = useState('');
  const [representanteCPF, setRepresentanteCPF] = useState('');
  
  // Campos de Endereço
  const [endereco, setEndereco] = useState('');
  const [numeroEndereco, setNumeroEndereco] = useState('');
  const [complemento, setComplemento] = useState('');
  const [cidade, setCidade] = useState('');
  const [estado, setEstado] = useState('');
  const [cep, setCep] = useState('');
  
  // Campos de Conta Bancária
  const [bancos, setBancos] = useState([]);
  const [bancoSelecionado, setBancoSelecionado] = useState('');
  const [agencia, setAgencia] = useState('');
  const [numeroConta, setNumeroConta] = useState('');
  const [tipoConta, setTipoConta] = useState('corrente');
  
  // Campos do titular da conta bancária (para modal)
  const [tipoPessoaBanco, setTipoPessoaBanco] = useState(() => {
    try { const u = getUsuarioLogado(); return u?.tipoPessoa || 'PF'; } catch { return 'PF'; }
  });
  const [nomeTitularBanco, setNomeTitularBanco] = useState('');
  const [documentoTitularBanco, setDocumentoTitularBanco] = useState('');
  
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');
  const [successMessage, setSuccessMessage] = useState('');
  const [buscandoCep, setBuscandoCep] = useState(false);
  
  // Formulário inline de conta bancária (para contas existentes)
  const [showBankForm, setShowBankForm] = useState(false);

  // Lista de estados brasileiros
  const estados = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
    'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
    'SP', 'SE', 'TO'
  ];

  // Verificar se usuário está logado e se já tem conta
  useEffect(() => {
    const token = localStorage.getItem('authToken');
    const user = getUsuarioLogado();
    
    setDebugInfo({
      hasToken: !!token,
      hasUser: !!user,
      userInfo: user
    });
    
    if (!isLoggedIn()) {
      navigate('/sidebar/Login');
      return;
    }
    
    if (user) {
      // Preencher automaticamente com dados do usuário logado
      setEmail(user.email || '');
      setTelefone(user.telefone || user.celular || '');
      setNome(user.nome || user.razaoSocial || user.nomeFantasia || '');
      setCpf(user.cpfUsuario || user.cpf || '');
      setCnpj(user.cnpjUsuario || user.cnpj || '');
      setTipoPessoa(user.tipoPessoa || user.TipoPessoa || 'PF');
      // Endereço
      setEndereco(user.endereco || '');
      setCidade(user.cidade || '');
      setEstado(user.estado || user.uf || '');
      setCep(user.cep || '');
    }
    
    checkExistingAccount();
    loadBancos();
  }, [navigate]);

  // Auto-popula campos do titular quando conta existe mas não tem banco
  // Volta do cadastro do Stripe: o status já foi recarregado ao abrir a tela; link vencido abre outro
  useEffect(() => {
    const retorno = new URLSearchParams(window.location.search).get('stripe');
    if (!retorno || !existingAccount?.stripeAccountId) return;
    window.history.replaceState(null, '', window.location.pathname);
    if (retorno === 'renovar') {
      verificarIdentidade();
    } else if (existingAccount.chargesEnabled && existingAccount.payoutsEnabled) {
      setSuccessMessage('✅ Verificação concluída: sua conta já pode receber pagamentos.');
    } else {
      setSuccessMessage('Dados enviados ao Stripe. A análise pode levar alguns minutos ou dias; clique em "Atualizar Status" para conferir.');
    }
    // Só uma vez, quando a conta carrega
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [existingAccount?.stripeAccountId]);

  useEffect(() => {
    if (existingAccount?.exists && !temDadosBancarios()) {
      const user = getUsuarioLogado();
      const isPJ = existingAccount?.businessType === 'company' || existingAccount?.tipoPessoa === 'PJ';
      setTipoPessoaBanco(isPJ ? 'PJ' : 'PF');
      setNomeTitularBanco(existingAccount?.empresaNome || nome || user?.nome || user?.razaoSocial || '');
      setDocumentoTitularBanco(
        isPJ
          ? (cnpj || user?.cnpj || user?.cnpjUsuario || '')
          : (cpf || user?.cpf || user?.cpfUsuario || '')
      );
    }
  }, [existingAccount]); // eslint-disable-line react-hooks/exhaustive-deps

  async function buscarCep(cepValor) {
    const cepLimpo = cepValor.replace(/\D/g, '');
    if (cepLimpo.length !== 8) return;
    setBuscandoCep(true);
    try {
      const res = await fetch(`https://viacep.com.br/ws/${cepLimpo}/json/`);
      const data = await res.json();
      if (data.erro) {
        setFormError('CEP não encontrado. Verifique o CEP digitado.');
        return;
      }
      setEndereco(data.logradouro || '');
      setCidade(data.localidade || '');
      setEstado(data.uf || '');
    } catch (err) {
      setFormError('Erro ao buscar CEP. Tente novamente.');
    } finally {
      setBuscandoCep(false);
    }
  }

  async function loadBancos() {
    try {
      const data = await apiCallAuth('StripeCustomAccount/banks', 'GET');
      setBancos(data || []);
    } catch (err) {
      console.log('Erro ao carregar bancos:', err.message);
      // Lista fallback de bancos
      setBancos([
        { code: '001', name: 'Banco do Brasil' },
        { code: '033', name: 'Santander' },
        { code: '104', name: 'Caixa Econômica Federal' },
        { code: '237', name: 'Bradesco' },
        { code: '341', name: 'Itaú' },
        { code: '077', name: 'Banco Inter' },
        { code: '260', name: 'Nubank' },
        { code: '336', name: 'C6 Bank' },
        { code: '290', name: 'PagSeguro' },
        { code: '323', name: 'Mercado Pago' }
      ]);
    }
  }

  async function checkExistingAccount() {
    try {
      // Primeiro tenta o endpoint do CadastroConta
      const data = await apiCallAuth('CadastroConta/minha-conta', 'GET');
      if (data.exists) {
        // Se tem conta, busca status detalhado do Stripe
        if (data.stripeAccountId) {
          try {
            const statusData = await apiCallAuth(`StripeCustomAccount/${data.stripeAccountId}/status`, 'GET');
            // Determinar tipoPessoa baseado no businessType do Stripe
            const tipoPessoaFromStripe = statusData.businessType === 'company' ? 'PJ' : 'PF';
            setExistingAccount({
              ...data,
              ...statusData,
              tipoPessoa: tipoPessoaFromStripe,
              exists: true
            });
            // Atualizar tipoPessoa e tipoPessoaBanco com base no Stripe
            setTipoPessoa(tipoPessoaFromStripe);
            setTipoPessoaBanco(tipoPessoaFromStripe);
          } catch {
            setExistingAccount(data);
          }
        } else {
          setExistingAccount(data);
        }
      }
    } catch (err) {
      console.log('Nenhuma conta existente:', err.message);
    } finally {
      setLoading(false);
    }
  }

  // Validação por etapa
  function validateStep(stepNumber) {
    setFormError('');
    
    if (stepNumber === 1) {
      if (!nome.trim()) {
        setFormError('Informe seu nome completo ou razão social.');
        return false;
      }
      if (!email.trim()) {
        setFormError('Informe seu e-mail.');
        return false;
      }
      if (tipoPessoa === 'PF' && !cpf.trim()) {
        setFormError('Informe seu CPF.');
        return false;
      }
      if (tipoPessoa === 'PJ' && !cnpj.trim()) {
        setFormError('Informe o CNPJ.');
        return false;
      }
      if (tipoPessoa === 'PJ' && !representanteNome.trim()) {
        setFormError('Informe o nome do representante legal.');
        return false;
      }
    }
    
    if (stepNumber === 2) {
      if (!endereco.trim()) {
        setFormError('Informe o endereço.');
        return false;
      }
      if (!numeroEndereco.trim()) {
        setFormError('Informe o número do endereço.');
        return false;
      }
      if (!cidade.trim()) {
        setFormError('Informe a cidade.');
        return false;
      }
      if (!estado) {
        setFormError('Selecione o estado.');
        return false;
      }
      if (!cep.trim()) {
        setFormError('Informe o CEP.');
        return false;
      }
    }
    
    if (stepNumber === 3) {
      if (!bancoSelecionado) {
        setFormError('Selecione o banco.');
        return false;
      }
      if (!agencia.trim()) {
        setFormError('Informe a agência.');
        return false;
      }
      if (!numeroConta.trim()) {
        setFormError('Informe o número da conta.');
        return false;
      }
    }
    
    return true;
  }

  function nextStep() {
    if (validateStep(step)) {
      setStep(step + 1);
    }
  }

  function prevStep() {
    setFormError('');
    setStep(step - 1);
  }

  async function criarContaRecebimento() {
    if (!validateStep(3)) return;
    
    setFormError('');
    setSuccessMessage('');
    setFormLoading(true);
    
    try {
      // 1. Criar conta Stripe Custom com todos os dados
      const createBody = {
        Email: email,
        Telefone: telefone,
        EmpresaId: empresaId || null,
        UsuarioSistemaId: debugInfo.userInfo?.id || null,
        // PF
        CPF: tipoPessoa === 'PF' ? cpf : null,
        NomeCompleto: tipoPessoa === 'PF' ? nome : null,
        DataNascimento: tipoPessoa === 'PF' && dataNascimento ? dataNascimento : null,
        // PJ
        CNPJ: tipoPessoa === 'PJ' ? cnpj : null,
        RazaoSocial: tipoPessoa === 'PJ' ? nome : null,
        RepresentanteNome: tipoPessoa === 'PJ' ? representanteNome : null,
        RepresentanteCPF: tipoPessoa === 'PJ' ? representanteCPF : null,
        // Endereço
        Endereco: `${endereco}${numeroEndereco ? ', ' + numeroEndereco : ''}${complemento ? ' - ' + complemento : ''}`,
        Cidade: cidade,
        Estado: estado,
        CEP: cep
      };
      
      console.log('Criando conta Stripe Custom:', createBody);
      const stripeData = await apiCallAuth('StripeCustomAccount/create', 'POST', createBody);
      
      if (!stripeData.success || !stripeData.stripeAccountId) {
        throw new Error(stripeData.error || 'Erro ao criar conta Stripe.');
      }
      
      console.log('Conta Stripe criada:', stripeData.stripeAccountId);
      
      // 2. Adicionar conta bancária à conta Stripe
      const bancoInfo = bancos.find(b => b.code === bancoSelecionado);
      const bankBody = {
        BankCode: bancoSelecionado,
        AgencyNumber: agencia.replace(/\D/g, ''),
        AccountNumber: numeroConta.replace(/\D/g, ''),
        AccountHolderName: nome,
        AccountHolderDocument: tipoPessoa === 'PF' ? cpf : cnpj,
        AccountHolderType: tipoPessoa === 'PF' ? 'individual' : 'company'
      };
      
      console.log('Adicionando conta bancária:', bankBody);
      const bankData = await apiCallAuth(`StripeCustomAccount/${stripeData.stripeAccountId}/bank-account`, 'POST', bankBody);
      
      if (!bankData.success) {
        // Conta criada mas banco falhou - ainda assim sucesso parcial
        setSuccessMessage('✅ Conta criada! Porém houve um problema ao cadastrar o banco. Você pode adicionar depois.');
      } else {
        setSuccessMessage('✅ Conta de recebimento criada com sucesso! Banco cadastrado: ' + (bancoInfo?.name || bancoSelecionado));
      }
      
      // 3. Atualizar estado com conta criada
      setExistingAccount({
        exists: true,
        stripeAccountId: stripeData.stripeAccountId,
        status: stripeData.status || 'pending_verification',
        chargesEnabled: stripeData.chargesEnabled,
        payoutsEnabled: stripeData.payoutsEnabled,
        empresaId: stripeData.empresaId,
        tipoPessoa: tipoPessoa,
        businessType: tipoPessoa === 'PJ' ? 'company' : 'individual',
        empresaNome: nome,
        bankName: bankData.bankName || bancoInfo?.name,
        bankLast4: bankData.last4,
        bankAccountId: bankData.externalAccountId
      });
      
      setStep(4); // Ir para confirmação
      
      if (typeof onSuccess === 'function') onSuccess(stripeData);
      
    } catch (err) {
      console.error('Erro ao criar conta:', err);
      setFormError(err.message || String(err));
      if (typeof onError === 'function') onError(err);
    } finally {
      setFormLoading(false);
    }
  }

  async function checkStatus() {
    // Resolver possíveis nomes de campo retornados pelo backend
    const accountId = existingAccount?.stripeAccountId || existingAccount?.accountId || existingAccount?.stripe_account_id || existingAccount?.id;
    if (!accountId) {
      setFormError('ID da conta Stripe não encontrado para atualizar o status.');
      return;
    }

    setFormError('');
    setFormLoading(true);
    try {
      const data = await apiCallAuth(`StripeCustomAccount/${accountId}/status`, 'GET');
      // Determinar tipoPessoa baseado no businessType do Stripe
      const tipoPessoaFromStripe = data.businessType === 'company' ? 'PJ' : 'PF';
      setExistingAccount(prev => ({
        ...prev,
        ...data,
        stripeAccountId: data.stripeAccountId || data.accountId || accountId,
        tipoPessoa: tipoPessoaFromStripe,
        exists: true
      }));
      // Sincronizar tipoPessoa/tipoPessoaBanco
      setTipoPessoa(tipoPessoaFromStripe);
      setTipoPessoaBanco(tipoPessoaFromStripe);
      setSuccessMessage('Status atualizado!');
    } catch (err) {
      setFormError(err.message || String(err));
    } finally {
      setFormLoading(false);
    }
  }

  async function verificarIdentidade() {
    const accountId = existingAccount?.stripeAccountId || existingAccount?.accountId || existingAccount?.stripe_account_id || existingAccount?.id;
    if (!accountId) {
      setFormError('ID da conta Stripe não encontrado para iniciar verificação.');
      return;
    }

    setFormLoading(true);
    setFormError('');
    try {
      // Cadastro do Stripe: pede o que falta (documento com foto, dados, termos) e volta para esta tela
      const data = await apiCallAuth(`StripeCustomAccount/${accountId}/onboarding-link`, 'POST');
      if (data.url) {
        window.location.href = data.url;
      } else {
        setFormError(data.error || 'Não foi possível abrir a verificação do Stripe.');
      }
    } catch (err) {
      setFormError(err.message || String(err));
    } finally {
      setFormLoading(false);
    }
  }

  // Forçar auto-verificação (apenas em modo de teste sk_test_)
  async function forcarVerificacaoTeste() {
    const accountId = existingAccount?.stripeAccountId || existingAccount?.accountId || existingAccount?.stripe_account_id || existingAccount?.id;
    if (!accountId) {
      setFormError('ID da conta Stripe não encontrado.');
      return;
    }

    setFormLoading(true);
    setFormError('');
    setSuccessMessage('');
    try {
      // Se for PJ (company), primeiro tenta criar as roles de teste (owner, director, executive)
      const isPJ = existingAccount?.businessType === 'company' || existingAccount?.tipoPessoa === 'PJ' || tipoPessoa === 'PJ';
      
      if (isPJ) {
        try {
          console.log('Criando roles de teste para PJ...');
          await apiCallAuth(`StripeCustomAccount/${accountId}/create-test-roles`, 'POST');
        } catch (roleErr) {
          console.log('create-test-roles falhou (pode já existir):', roleErr.message);
        }
      }

      // Força auto-verificação
      const data = await apiCallAuth(`StripeCustomAccount/${accountId}/force-auto-verify`, 'POST');
      if (data.success) {
        setSuccessMessage(`✅ ${data.message || 'Verificação forçada com sucesso!'} Charges: ${data.chargesEnabled ? 'Ativo' : 'Pendente'}, Payouts: ${data.payoutsEnabled ? 'Ativo' : 'Pendente'}`);
        // Atualizar status após forçar verificação
        await checkStatus();
      } else {
        setFormError(data.error || 'Erro ao forçar verificação.');
      }
    } catch (err) {
      setFormError(err.message || String(err));
    } finally {
      setFormLoading(false);
    }
  }

  // Verifica se a conta existente tem dados bancários cadastrados
  function temDadosBancarios() {
    if (!existingAccount) return false;
    // Checa várias formas que o backend pode retornar dados bancários
    if (existingAccount.bankName) return true;
    if (existingAccount.bankAccountId) return true;
    if (existingAccount.BankAccountLast4 || existingAccount.bankAccountLast4 || existingAccount.bankLast4) return true;
    // Backend retorna externalAccounts como array de contas bancárias
    if (existingAccount.externalAccounts && Array.isArray(existingAccount.externalAccounts) && existingAccount.externalAccounts.length > 0) {
      // Verificar se alguma external account tem dados válidos
      return existingAccount.externalAccounts.some(ea => ea.id || ea.last4);
    }
    return false;
  }

  // Cadastrar conta bancária para conta existente
  async function cadastrarContaBancaria() {
    const accountId = existingAccount?.stripeAccountId || existingAccount?.accountId || existingAccount?.stripe_account_id;
    if (!accountId) {
      setFormError('ID da conta Stripe não encontrado para cadastrar banco.');
      return;
    }

    if (!bancoSelecionado) {
      setFormError('Selecione o banco.');
      return;
    }
    if (!agencia.trim()) {
      setFormError('Informe a agência.');
      return;
    }
    if (!numeroConta.trim()) {
      setFormError('Informe o número da conta.');
      return;
    }

    // Validar campos do titular
    if (!nomeTitularBanco.trim()) {
      setFormError(tipoPessoaBanco === 'PJ' ? 'Informe a Razão Social.' : 'Informe o Nome Completo do Titular.');
      return;
    }
    if (!documentoTitularBanco.trim()) {
      setFormError(tipoPessoaBanco === 'PJ' ? 'Informe o CNPJ.' : 'Informe o CPF.');
      return;
    }

    setFormLoading(true);
    setFormError('');
    try {
      const bancoInfo = bancos.find(b => b.code === bancoSelecionado);
      
      const bankBody = {
        BankCode: bancoSelecionado,
        AgencyNumber: agencia.replace(/\D/g, ''),
        AccountNumber: numeroConta.replace(/\D/g, ''),
        AccountHolderName: nomeTitularBanco,
        AccountHolderDocument: documentoTitularBanco.replace(/\D/g, ''),
        AccountHolderType: tipoPessoaBanco === 'PJ' ? 'company' : 'individual'
      };

      console.log('Cadastrando conta bancária:', bankBody);
      const bankData = await apiCallAuth(`StripeCustomAccount/${accountId}/bank-account`, 'POST', bankBody);

      if (bankData.success) {
        setSuccessMessage('✅ Conta bancária cadastrada com sucesso! Banco: ' + (bankData.bankName || bancoInfo?.name));
        setExistingAccount(prev => ({
          ...prev,
          bankName: bankData.bankName || bancoInfo?.name,
          bankLast4: bankData.last4,
          bankAccountId: bankData.externalAccountId // Backend retorna externalAccountId
        }));
        setShowBankForm(false);
        // Limpar campos do formulário de banco
        setBancoSelecionado('');
        setAgencia('');
        setNumeroConta('');
        // Manter tipoPessoaBanco igual ao tipoPessoa do usuário
        const user = getUsuarioLogado();
        setTipoPessoaBanco(user?.tipoPessoa || 'PF');
        setNomeTitularBanco('');
        setDocumentoTitularBanco('');
      } else {
        throw new Error(bankData.error || 'Erro ao cadastrar conta bancária.');
      }
    } catch (err) {
      console.error('Erro ao cadastrar banco:', err);
      setFormError(err.message || String(err));
    } finally {
      setFormLoading(false);
    }
  }

  async function corrigirPendencias() {
    const accountId = existingAccount?.stripeAccountId || existingAccount?.accountId || existingAccount?.stripe_account_id || existingAccount?.id;
    if (!accountId) {
      setFormError('ID da conta Stripe não encontrado.');
      return;
    }
    setFormLoading(true);
    setFormError('');
    setSuccessMessage('');
    try {
      const data = await apiCallAuth(`StripeCustomAccount/${accountId}/fulfill-test-kyc`, 'POST');
      if (data.success !== false) {
        setSuccessMessage('✅ Campos pendentes corrigidos! Clique em "Atualizar Status" para ver o novo status.');
        await checkStatus();
      } else {
        setFormError(data.error || 'Erro ao corrigir pendências.');
      }
    } catch (err) {
      setFormError(err.message || String(err));
    } finally {
      setFormLoading(false);
    }
  }

  const getStatusBadge = (status, chargesEnabled, payoutsEnabled) => {
    // chargesEnabled + payoutsEnabled são a fonte da verdade do Stripe
    if (chargesEnabled && payoutsEnabled) {
      return (
        <span style={{ background: '#e8f5e9', color: '#2e7d32', padding: '6px 14px', borderRadius: 12, fontSize: 14, fontWeight: 500 }}>
          ✓ Ativa - Pronta para receber
        </span>
      );
    }
    const statusColors = {
      'active': { bg: '#e8f5e9', color: '#2e7d32', text: '✓ Ativa - Pronta para receber' },
      'charges_enabled': { bg: '#e8f5e9', color: '#2e7d32', text: '✓ Ativa - Pronta para receber' },
      'pending': { bg: '#fff8e1', color: '#ff8f00', text: '⏳ Aguardando verificação...' },
      'pending_verification': { bg: '#fff8e1', color: '#ff8f00', text: '⏳ Verificação pendente' },
      'created': { bg: '#e3f2fd', color: '#1565c0', text: '🆕 Conta criada' },
      'default': { bg: '#f5f5f5', color: '#757575', text: status || 'Verificando...' }
    };
    const s = statusColors[status] || statusColors['default'];
    return (
      <span style={{ 
        background: s.bg, 
        color: s.color, 
        padding: '6px 14px', 
        borderRadius: 12, 
        fontSize: 14,
        fontWeight: 500
      }}>
        {s.text}
      </span>
    );
  };

  // Indicador de progresso
  const StepIndicator = () => (
    <div style={{ display: 'flex', justifyContent: 'center', gap: 8, marginBottom: 24 }}>
      {[1, 2, 3].map(s => (
        <div 
          key={s}
          style={{
            width: 32,
            height: 32,
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            background: step >= s ? 'rgb(235, 133, 0)' : '#e0e0e0',
            color: step >= s ? 'white' : '#999',
            fontWeight: 600,
            fontSize: 14,
            transition: 'all 0.3s'
          }}
        >
          {s}
        </div>
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className="minha-conta-container">
        <div className="minha-conta-card">
          <p>Carregando...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="minha-conta-container" style={{ position: 'relative' }}>
      <button
        onClick={() => navigate('/sidebar/Configuracoes')}
        className="planos-btn-voltar"
        style={{ position: 'absolute', top: 10, left: 10, zIndex: 20 }}
        title="Voltar para Configurações"
      >
        <svg width="20" height="20" viewBox="0 0 24 24" fill="none"><path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/></svg>
        Voltar
      </button>
      <div className="minha-conta-card">
        <h2 style={{ marginTop: 0, color: 'rgb(235, 133, 0)', marginBottom: 8 }}>💳 Conta de Recebimento</h2>
        <p style={{ color: '#666', marginTop: 0, marginBottom: 20 }}>
          Configure sua conta para receber pagamentos das suas vendas.
        </p>

        {/* Debug Info - mostra status de autenticação */}
        {(!debugInfo.hasToken || !debugInfo.hasUser) && (
          <div style={{ 
            background: '#fff3cd', 
            border: '1px solid #ffc107',
            padding: 12, 
            borderRadius: 8, 
            marginBottom: 16 
          }}>
            <strong style={{ color: '#856404' }}>⚠️ Problema de autenticação:</strong>
            <div style={{ marginTop: 8 }}>
              <button 
                onClick={() => navigate('/sidebar/Login')} 
                style={{ 
                  padding: '8px 16px', 
                  background: 'rgb(235, 133, 0)', 
                  color: 'white', 
                  border: 'none', 
                  borderRadius: 4, 
                  cursor: 'pointer' 
                }}
              >
                Fazer Login
              </button>
            </div>
          </div>
        )}

        {/* Se já tem conta, mostrar status */}
        {existingAccount?.exists && (
          <div style={{ 
            background: '#f8f9fa', 
            padding: 20, 
            borderRadius: 10, 
            marginBottom: 20,
            border: '1px solid #e0e0e0'
          }}>
            <h3 style={{ margin: '0 0 16px 0', color: '#333', fontSize: 18 }}>✅ Sua Conta está Configurada</h3>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              <div>
                <strong>Tipo:</strong> {existingAccount.tipoPessoa === 'PJ' ? 'Pessoa Jurídica' : 'Pessoa Física'}
              </div>
              {existingAccount.empresaNome && (
                <div><strong>Nome/Empresa:</strong> {existingAccount.empresaNome}</div>
              )}
              {/* Mostrar dados bancários - priorizar externalAccounts do Stripe */}
              {existingAccount.externalAccounts?.length > 0 ? (
                <div><strong>Banco:</strong> {existingAccount.externalAccounts[0].bankName || 'Cadastrado'} {existingAccount.externalAccounts[0].last4 ? `(****${existingAccount.externalAccounts[0].last4})` : ''}</div>
              ) : existingAccount.bankName ? (
                <div><strong>Banco:</strong> {existingAccount.bankName} {existingAccount.bankLast4 || existingAccount.bankAccountLast4 ? `(****${existingAccount.bankLast4 || existingAccount.bankAccountLast4})` : ''}</div>
              ) : null}
              <div style={{ marginTop: 8 }}>
                <strong>Status:</strong> {getStatusBadge(existingAccount.status, existingAccount.chargesEnabled, existingAccount.payoutsEnabled)}
              </div>
              {existingAccount.chargesEnabled !== undefined && (
                <div style={{ marginTop: 4, fontSize: 13, color: '#666' }}>
                  Receber pagamentos: {existingAccount.chargesEnabled ? '✅ Habilitado' : '❌ Pendente'}
                  {' | '}
                  Saques: {existingAccount.payoutsEnabled ? '✅ Habilitado' : '❌ Pendente'}
                </div>
              )}
            </div>
            
            {/* Requisitos pendentes */}
            {existingAccount.requirements?.currentlyDue?.length > 0 && (
              <div style={{ marginTop: 16, padding: 12, background: '#fff8e1', borderRadius: 8 }}>
                <strong style={{ color: '#ff8f00' }}>⚠️ Documentos pendentes:</strong>
                <ul style={{ margin: '8px 0 0 0', paddingLeft: 20, fontSize: 13 }}>
                  {existingAccount.requirements.currentlyDue.slice(0, 5).map((req, i) => (
                    <li key={i}>{req}</li>
                  ))}
                </ul>
              </div>
            )}
            
            {/* AVISO: Sem dados bancários */}
            {!temDadosBancarios() && (
              <div style={{ marginTop: 16, padding: 14, background: '#ffebee', borderRadius: 8, border: '1px solid #ef9a9a' }}>
                <strong style={{ color: '#c62828' }}>⚠️ Dados bancários não cadastrados!</strong>
                <p style={{ margin: '8px 0 0 0', fontSize: 13, color: '#b71c1c' }}>
                  Você precisa cadastrar uma conta bancária para receber os pagamentos das suas vendas.
                </p>
              </div>
            )}
            
            <div style={{ marginTop: 20, display: 'flex', gap: 12, flexWrap: 'wrap' }}>
              <button 
                type="button" 
                className="btn-secondary" 
                onClick={checkStatus}
                disabled={formLoading}
                style={{ padding: '10px 20px' }}
              >
                {formLoading ? '⏳...' : '🔄 Atualizar Status'}
              </button>
              
              {!(existingAccount.chargesEnabled && existingAccount.payoutsEnabled) && (
                <button 
                  type="button" 
                  className="btn-primary" 
                  onClick={verificarIdentidade}
                  disabled={formLoading}
                  style={{ 
                    padding: '10px 20px',
                    background: formLoading ? '#ccc' : '#1976d2',
                    color: 'white'
                  }}
                >
                  {formLoading ? '⏳...' : '📄 Verificar Identidade'}
                </button>
              )}
              
              {/* Botão corrigir pendências com dados de teste (só no modo teste) */}
              {existingAccount.modoTeste && existingAccount.requirements?.currentlyDue?.length > 0 && !existingAccount.chargesEnabled && (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={corrigirPendencias}
                  disabled={formLoading}
                  style={{
                    padding: '10px 20px',
                    background: formLoading ? '#ccc' : '#e65100',
                    color: 'white'
                  }}
                  title="Envia os campos pendentes (mcc, product_description, political_exposure) para a conta existente"
                >
                  {formLoading ? '⏳...' : '🛠️ Corrigir Pendências'}
                </button>
              )}

              {/* Botão para forçar verificação em modo de teste (só aparece se não está ativo) */}
              {existingAccount.modoTeste && existingAccount.status !== 'active' && !existingAccount.chargesEnabled && (
                <button 
                  type="button" 
                  className="btn-primary" 
                  onClick={forcarVerificacaoTeste}
                  disabled={formLoading}
                  style={{ 
                    padding: '10px 20px',
                    background: formLoading ? '#ccc' : '#9c27b0',
                    color: 'white'
                  }}
                  title="Apenas em modo de teste (sk_test_)"
                >
                  {formLoading ? '⏳...' : '🧪 Forçar Verificação (Teste)'}
                </button>
              )}
              
              {/* Botão alterar dados bancários (só quando já tem banco) */}
              {temDadosBancarios() && (
                <button
                  type="button"
                  className="btn-secondary"
                  onClick={() => {
                    const user = getUsuarioLogado();
                    const isPJ = existingAccount?.businessType === 'company' || existingAccount?.tipoPessoa === 'PJ' || tipoPessoa === 'PJ';
                    setTipoPessoaBanco(isPJ ? 'PJ' : 'PF');
                    setNomeTitularBanco(existingAccount?.empresaNome || nome || user?.nome || user?.razaoSocial || '');
                    setDocumentoTitularBanco(
                      isPJ
                        ? (cnpj || user?.cnpj || user?.cnpjUsuario || '')
                        : (cpf || user?.cpf || user?.cpfUsuario || '')
                    );
                    setShowBankForm(!showBankForm);
                  }}
                  disabled={formLoading}
                  style={{ padding: '10px 20px' }}
                >
                  🔄 Alterar Dados Bancários
                </button>
              )}
            </div>
            
            {existingAccount.chargesEnabled && existingAccount.payoutsEnabled && (
              <div style={{ marginTop: 20, padding: 16, background: '#e8f5e9', borderRadius: 8 }}>
                <p style={{ margin: 0, color: '#2e7d32', fontSize: 14 }}>
                  🎉 Tudo pronto! Agora você pode vender seus produtos e receber pagamentos via PIX, cartão ou boleto.
                </p>
              </div>
            )}

            {/* FORMULÁRIO INLINE: banco pendente ou alteração */}
            {(!temDadosBancarios() || showBankForm) && (
              <div style={{ marginTop: 24, padding: 20, background: '#fff', border: '1px solid #e0e0e0', borderRadius: 10 }}>
                {!temDadosBancarios() && (
                  <div style={{ marginBottom: 16, padding: 14, background: '#fff8e1', borderRadius: 8, border: '1px solid #ffe082' }}>
                    <strong style={{ color: '#f57f17' }}>⚠️ Dados bancários pendentes</strong>
                    <p style={{ margin: '6px 0 0 0', fontSize: 13, color: '#7f6000' }}>
                      Conta Stripe criada, mas a conta bancária ainda não foi cadastrada. Preencha abaixo para concluir.
                    </p>
                  </div>
                )}

                <h4 style={{ margin: '0 0 16px 0', color: '#333' }}>
                  🏦 {temDadosBancarios() ? 'Alterar' : 'Cadastrar'} Conta Bancária
                </h4>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                  {/* Tipo Pessoa - somente leitura baseado na conta existente */}
                  <div>
                    <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 8, fontWeight: 500 }}>Tipo de Conta</label>
                    <div style={{ display: 'flex', gap: 16 }}>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input type="radio" name="tipoPessoaBancoInline" value="PF" checked={tipoPessoaBanco === 'PF'} onChange={(e) => setTipoPessoaBanco(e.target.value)} />
                        <span>Pessoa Física (CPF)</span>
                      </label>
                      <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input type="radio" name="tipoPessoaBancoInline" value="PJ" checked={tipoPessoaBanco === 'PJ'} onChange={(e) => setTipoPessoaBanco(e.target.value)} />
                        <span>Pessoa Jurídica (CNPJ)</span>
                      </label>
                    </div>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>
                        {tipoPessoaBanco === 'PJ' ? 'Razão Social *' : 'Nome Completo do Titular *'}
                      </label>
                      <input value={nomeTitularBanco} onChange={(e) => setNomeTitularBanco(e.target.value)} className="form-input" placeholder={tipoPessoaBanco === 'PJ' ? 'Razão Social' : 'Nome completo'} style={{ width: '100%', padding: '12px', fontSize: 14 }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>
                        {tipoPessoaBanco === 'PJ' ? 'CNPJ *' : 'CPF *'}
                      </label>
                      <input value={documentoTitularBanco} onChange={(e) => setDocumentoTitularBanco(e.target.value)} className="form-input" placeholder={tipoPessoaBanco === 'PJ' ? '00.000.000/0000-00' : '000.000.000-00'} style={{ width: '100%', padding: '12px', fontSize: 14 }} />
                    </div>
                  </div>

                  <div>
                    <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>Banco *</label>
                    <select value={bancoSelecionado} onChange={(e) => setBancoSelecionado(e.target.value)} className="form-input" style={{ width: '100%', padding: '12px', fontSize: 14 }}>
                      <option value="">Selecione o banco...</option>
                      {bancos.map(b => (<option key={b.code} value={b.code}>{b.name}</option>))}
                    </select>
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>Agência *</label>
                      <input value={agencia} onChange={(e) => setAgencia(e.target.value)} className="form-input" placeholder="0000" style={{ width: '100%', padding: '12px', fontSize: 14 }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>Número da Conta *</label>
                      <input value={numeroConta} onChange={(e) => setNumeroConta(e.target.value)} className="form-input" placeholder="00000-0" style={{ width: '100%', padding: '12px', fontSize: 14 }} />
                    </div>
                    <div>
                      <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>Tipo de Conta</label>
                      <select value={tipoConta} onChange={(e) => setTipoConta(e.target.value)} className="form-input" style={{ width: '100%', padding: '12px', fontSize: 14 }}>
                        <option value="corrente">Corrente</option>
                        <option value="poupanca">Poupança</option>
                      </select>
                    </div>
                  </div>
                </div>

                {formError && (
                  <div style={{ color: '#d32f2f', marginTop: 16, padding: 12, background: '#ffebee', borderRadius: 8, fontSize: 13 }}>⚠️ {formError}</div>
                )}
                {successMessage && (
                  <div style={{ color: '#2e7d32', marginTop: 16, padding: 12, background: '#e8f5e9', borderRadius: 8, fontSize: 13 }}>{successMessage}</div>
                )}

                <div style={{ marginTop: 20, display: 'flex', gap: 12, justifyContent: 'flex-end' }}>
                  {temDadosBancarios() && (
                    <button type="button" onClick={() => { setShowBankForm(false); setFormError(''); }} style={{ padding: '12px 24px', fontSize: 14, background: 'transparent', border: '2px solid #ccc', borderRadius: 8, color: '#666', cursor: 'pointer', fontWeight: 500 }}>
                      Cancelar
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={cadastrarContaBancaria}
                    disabled={formLoading || !bancoSelecionado || !agencia || !numeroConta || !nomeTitularBanco.trim() || !documentoTitularBanco.trim()}
                    style={{ padding: '12px 24px', fontSize: 14, background: (formLoading || !bancoSelecionado || !agencia || !numeroConta || !nomeTitularBanco.trim() || !documentoTitularBanco.trim()) ? '#ccc' : '#4caf50', border: 'none', borderRadius: 8, color: 'white', cursor: 'pointer', fontWeight: 600 }}
                  >
                    {formLoading ? '⏳ Salvando...' : '✅ Salvar Conta Bancária'}
                  </button>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Formulário de criação (só mostra se não tem conta) */}
        {!existingAccount?.exists && (
          <>
            <StepIndicator />
            
            {/* ETAPA 1: Dados Pessoais */}
            {step === 1 && (
              <>
                <h3 style={{ margin: '0 0 16px 0', color: '#333' }}>📋 Dados Pessoais</h3>
                
                {/* Tipo de Pessoa */}
                <div style={{ marginBottom: 20 }}>
                  <label style={{ display: 'block', fontSize: 14, color: '#333', marginBottom: 8, fontWeight: 500 }}>
                    Tipo de Conta *
                  </label>
                  <div style={{ display: 'flex', gap: 16 }}>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input 
                          type="radio" 
                          name="tipoPessoa" 
                          value="PF" 
                          checked={tipoPessoa === 'PF'}
                          onChange={(e) => setTipoPessoa(e.target.value)}
                        />
                      <span>Pessoa Física (CPF)</span>
                    </label>
                    <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                        <input 
                          type="radio" 
                          name="tipoPessoa" 
                          value="PJ" 
                          checked={tipoPessoa === 'PJ'}
                          onChange={(e) => setTipoPessoa(e.target.value)}
                        />
                      <span>Pessoa Jurídica (CNPJ)</span>
                    </label>
                  </div>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: 16, marginBottom: 20 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>
                      {tipoPessoa === 'PJ' ? 'Razão Social *' : 'Nome Completo *'}
                    </label>
                    <input 
                      value={nome} 
                      onChange={(e) => setNome(e.target.value)} 
                      className="form-input" 
                      placeholder={tipoPessoa === 'PJ' ? 'Razão Social da Empresa' : 'Seu nome completo'}
                      style={{ width: '100%', padding: '12px', fontSize: 14 }}
                    />
                  </div>
                  
                  {tipoPessoa === 'PF' ? (
                    <>
                      <div>
                        <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>CPF *</label>
                        <input 
                          value={cpf} 
                          onChange={(e) => setCpf(e.target.value)} 
                          className="form-input" 
                          placeholder="000.000.000-00"
                          style={{ width: '100%', padding: '12px', fontSize: 14 }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>Data de Nascimento</label>
                        <input 
                          type="date"
                          value={dataNascimento} 
                          onChange={(e) => setDataNascimento(e.target.value)} 
                          className="form-input" 
                          style={{ width: '100%', padding: '12px', fontSize: 14 }}
                        />
                      </div>
                    </>
                  ) : (
                    <>
                      <div>
                        <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>CNPJ *</label>
                        <input 
                          value={cnpj} 
                          onChange={(e) => setCnpj(e.target.value)} 
                          className="form-input" 
                          placeholder="00.000.000/0000-00"
                          style={{ width: '100%', padding: '12px', fontSize: 14 }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>Nome do Representante Legal *</label>
                        <input 
                          value={representanteNome} 
                          onChange={(e) => setRepresentanteNome(e.target.value)} 
                          className="form-input" 
                          placeholder="Nome completo do representante"
                          style={{ width: '100%', padding: '12px', fontSize: 14 }}
                        />
                      </div>
                      <div>
                        <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>CPF do Representante</label>
                        <input 
                          value={representanteCPF} 
                          onChange={(e) => setRepresentanteCPF(e.target.value)} 
                          className="form-input" 
                          placeholder="000.000.000-00"
                          style={{ width: '100%', padding: '12px', fontSize: 14 }}
                        />
                      </div>
                    </>
                  )}

                  <div>
                    <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>E-mail *</label>
                    <input 
                      value={email} 
                      onChange={(e) => setEmail(e.target.value)} 
                      className="form-input" 
                      type="email"
                      placeholder="seu@email.com"
                      style={{ width: '100%', padding: '12px', fontSize: 14 }}
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>Telefone</label>
                    <input 
                      value={telefone} 
                      onChange={(e) => setTelefone(e.target.value)} 
                      className="form-input" 
                      placeholder="(00) 00000-0000"
                      style={{ width: '100%', padding: '12px', fontSize: 14 }}
                    />
                  </div>
                </div>
              </>
            )}

            {/* ETAPA 2: Endereço */}
            {step === 2 && (
              <>
                <h3 style={{ margin: '0 0 16px 0', color: '#333' }}>📍 Endereço</h3>
                
                <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16, marginBottom: 20 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>Endereço (Rua) *</label>
                    <input 
                      value={endereco} 
                      onChange={(e) => setEndereco(e.target.value)} 
                      className="form-input" 
                      placeholder="Rua das Flores"
                      style={{ width: '100%', padding: '12px', fontSize: 14 }}
                    />
                  </div>

                  <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: 16 }}>
                    <div style={{ maxWidth: 140 }}>
                      <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>Número *</label>
                      <input 
                        value={numeroEndereco} 
                        onChange={(e) => setNumeroEndereco(e.target.value)} 
                        className="form-input" 
                        placeholder="123"
                        style={{ width: '100%', padding: '12px', fontSize: 14 }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>Complemento</label>
                      <input 
                        value={complemento} 
                        onChange={(e) => setComplemento(e.target.value)} 
                        className="form-input" 
                        placeholder="Apto 45 / Bloco B"
                        style={{ width: '100%', padding: '12px', fontSize: 14 }}
                      />
                    </div>

                    <div style={{ maxWidth: 140 }}>
                      <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>
                        CEP * {buscandoCep && <span style={{ color: '#888', fontSize: 12 }}>🔍 buscando...</span>}
                      </label>
                      <input 
                        value={cep} 
                        onChange={(e) => {
                          const val = e.target.value;
                          setCep(val);
                          const nums = val.replace(/\D/g, '');
                          if (nums.length === 8) buscarCep(nums);
                        }}
                        className="form-input" 
                        placeholder="00000-000"
                        maxLength={9}
                        style={{ width: '100%', padding: '12px', fontSize: 14 }}
                      />
                    </div>

                    <div>
                      <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>Cidade *</label>
                      <input 
                        value={cidade} 
                        onChange={(e) => setCidade(e.target.value)} 
                        className="form-input" 
                        placeholder="São Paulo"
                        style={{ width: '100%', padding: '12px', fontSize: 14 }}
                      />
                    </div>

                    <div style={{ maxWidth: 120 }}>
                      <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>UF *</label>
                      <select 
                        value={estado} 
                        onChange={(e) => setEstado(e.target.value)} 
                        className="form-input"
                        style={{ width: '100%', padding: '12px', fontSize: 14 }}
                      >
                        <option value="">Selecione...</option>
                        {estados.map(uf => (
                          <option key={uf} value={uf}>{uf}</option>
                        ))}
                      </select>
                    </div>
                  </div>
                </div>
              </>
            )}

            {/* ETAPA 3: Conta Bancária */}
            {step === 3 && (
              <>
                <h3 style={{ margin: '0 0 16px 0', color: '#333' }}>🏦 Conta Bancária para Recebimentos</h3>
                
                <div style={{ 
                  background: '#e3f2fd', 
                  padding: 12, 
                  borderRadius: 8, 
                  marginBottom: 20,
                  border: '1px solid #90caf9'
                }}>
                  <p style={{ margin: 0, color: '#1565c0', fontSize: 13 }}>
                    ℹ️ Informe a conta bancária onde você deseja receber os pagamentos das suas vendas.
                  </p>
                </div>
                
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 16, marginBottom: 20 }}>
                  <div>
                    <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>Banco *</label>
                    <select 
                      value={bancoSelecionado} 
                      onChange={(e) => setBancoSelecionado(e.target.value)} 
                      className="form-input"
                      style={{ width: '100%', padding: '12px', fontSize: 14 }}
                    >
                      <option value="">Selecione o banco...</option>
                      {bancos.map(b => (
                        <option key={b.code} value={b.code}>{b.name}</option>
                      ))}
                    </select>
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>Agência *</label>
                    <input 
                      value={agencia} 
                      onChange={(e) => setAgencia(e.target.value)} 
                      className="form-input" 
                      placeholder="0000"
                      style={{ width: '100%', padding: '12px', fontSize: 14 }}
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>Número da Conta *</label>
                    <input 
                      value={numeroConta} 
                      onChange={(e) => setNumeroConta(e.target.value)} 
                      className="form-input" 
                      placeholder="00000-0"
                      style={{ width: '100%', padding: '12px', fontSize: 14 }}
                    />
                  </div>
                  
                  <div>
                    <label style={{ display: 'block', fontSize: 13, color: '#444', marginBottom: 6 }}>Tipo de Conta *</label>
                    <select 
                      value={tipoConta} 
                      onChange={(e) => setTipoConta(e.target.value)} 
                      className="form-input"
                      style={{ width: '100%', padding: '12px', fontSize: 14 }}
                    >
                      <option value="corrente">Conta Corrente</option>
                      <option value="poupanca">Conta Poupança</option>
                    </select>
                  </div>
                </div>
                
                {/* Resumo antes de criar */}
                <div style={{ background: '#f5f5f5', padding: 16, borderRadius: 8, marginTop: 20 }}>
                  <h4 style={{ margin: '0 0 12px 0', color: '#333' }}>📝 Resumo</h4>
                  <div style={{ fontSize: 14, color: '#555', lineHeight: 1.8 }}>
                    <div><strong>Titular:</strong> {nome}</div>
                    <div><strong>Documento:</strong> {tipoPessoa === 'PF' ? cpf : cnpj}</div>
                    <div><strong>Endereço:</strong> {endereco}, {cidade}/{estado}</div>
                    <div><strong>Banco:</strong> {bancos.find(b => b.code === bancoSelecionado)?.name || bancoSelecionado}</div>
                    <div><strong>Agência:</strong> {agencia} | <strong>Conta:</strong> {numeroConta}</div>
                  </div>
                </div>
              </>
            )}

            {/* ETAPA 4: Confirmação (sucesso) */}
            {step === 4 && (
              <div style={{ textAlign: 'center', padding: 20 }}>
                <div style={{ fontSize: 64, marginBottom: 16 }}>🎉</div>
                <h3 style={{ color: '#2e7d32', marginBottom: 16 }}>Conta Criada com Sucesso!</h3>
                <p style={{ color: '#666', marginBottom: 20 }}>
                  Sua conta de recebimento está configurada. Agora você pode começar a vender!
                </p>
                <button 
                  onClick={() => window.location.reload()}
                  style={{
                    padding: '12px 24px',
                    background: 'rgb(235, 133, 0)',
                    color: 'white',
                    border: 'none',
                    borderRadius: 8,
                    cursor: 'pointer',
                    fontWeight: 600
                  }}
                >
                  Ver Status da Conta
                </button>
              </div>
            )}

            {/* Mensagens de erro/sucesso */}
            {formError && (
              <div style={{ 
                color: '#d32f2f', 
                marginTop: 16, 
                padding: 14, 
                background: '#ffebee', 
                borderRadius: 8,
                fontSize: 14
              }}>
                ⚠️ {formError}
              </div>
            )}

            {successMessage && (
              <div style={{ 
                color: '#2e7d32', 
                marginTop: 16, 
                padding: 14, 
                background: '#e8f5e9', 
                borderRadius: 8,
                fontSize: 14
              }}>
                {successMessage}
              </div>
            )}

            {/* Botões de navegação */}
            {step < 4 && (
              <div style={{ marginTop: 24, display: 'flex', justifyContent: 'space-between', gap: 16 }}>
                {step > 1 ? (
                  <button 
                    type="button" 
                    onClick={prevStep}
                    style={{ 
                      padding: '12px 24px', 
                      fontSize: 15,
                      background: 'transparent',
                      border: '2px solid #ccc',
                      borderRadius: 8,
                      color: '#666',
                      cursor: 'pointer',
                      fontWeight: 500
                    }}
                  >
                    ← Voltar
                  </button>
                ) : <div />}
                
                {step < 3 ? (
                  <button 
                    type="button" 
                    onClick={nextStep}
                    style={{ 
                      padding: '12px 32px', 
                      fontSize: 15,
                      background: 'rgb(235, 133, 0)',
                      border: 'none',
                      borderRadius: 8,
                      color: 'white',
                      cursor: 'pointer',
                      fontWeight: 600
                    }}
                  >
                    Próximo →
                  </button>
                ) : (
                  <button 
                    type="button" 
                    onClick={criarContaRecebimento} 
                    disabled={formLoading}
                    style={{ 
                      padding: '14px 32px', 
                      fontSize: 16,
                      background: formLoading ? '#ccc' : '#4caf50',
                      border: 'none',
                      borderRadius: 8,
                      color: 'white',
                      cursor: formLoading ? 'not-allowed' : 'pointer',
                      fontWeight: 600
                    }}
                  >
                    {formLoading ? '⏳ Criando conta...' : '✅ Criar Conta de Recebimento'}
                  </button>
                )}
              </div>
            )}
          </>
        )}

        {/* Informações adicionais */}
        <div style={{ marginTop: 30, padding: 20, background: '#f5f5f5', borderRadius: 10 }}>
          <h4 style={{ margin: '0 0 12px 0', color: '#555' }}>ℹ️ Como funciona?</h4>
          <ul style={{ margin: 0, paddingLeft: 20, color: '#666', fontSize: 14, lineHeight: 1.8 }}>
            <li>Cadastre seus dados pessoais e endereço</li>
            <li>Informe a conta bancária para recebimentos</li>
            <li>Venda seus produtos normalmente</li>
            <li>Os clientes pagam via PIX, cartão ou boleto</li>
            <li>O dinheiro cai direto na sua conta bancária!</li>
          </ul>
        </div>
      </div>
      
    </div>
  );
}
